/**
 * @file planService.js
 * NUEVA ARQUITECTURA DUAL - Frontend PlanService
 * 
 * CARACTERÍSTICAS:
 * - Maneja plan_data_pure (N8N) y plan_data_visual (Editor)
 * - Conversión automática entre formatos
 * - Auto-generación de datos visuales
 */

import postgresBackendService from './postgresBackendService';
import { PlanModel } from './models/plan';

class PlanService {
  constructor() {
    this.provider = postgresBackendService;
  }

  /**
   * Obtener planes filtrados por usuario
   */
  async getPlans(userId) {
    try {
      console.log('[PlanService] getPlans - INICIO', { userId: userId?.substring(0, 6) + '...' });
      
      // El backend ya devuelve solo los planes del usuario del JWT (#576). El filtro que
      // había aquí admitía además los que no tienen dueño (`!plan.userId`), justo los
      // huérfanos que #573 dejó de servir: contradecía al backend y no protegía de nada,
      // porque un filtro en el cliente no es una frontera de seguridad.
      const plans = await this.provider.getPlans();

      console.log('[PlanService] getPlans - planes recibidos:', plans.length);

      return {
        success: true,
        data: plans
      };
    } catch (error) {
      console.error('[PlanService] getPlans - ERROR:', error);
      throw error;
    }
  }

  /**
   * Obtener plan por ID - FORMATO PARA EDITOR
   */
  async getPlanById(id) {
    try {
      console.log('[PlanService] getPlanById - INICIO', { id });
      
      const planData = await this.provider.getPlanById(id);
      if (!planData) {
        throw new Error(`Plan ${id} no encontrado`);
      }
      
      // Crear modelo con nueva arquitectura
      const plan = new PlanModel(planData);
      
      // Convertir para el editor (con información visual)
      const editorFormat = plan.toEditorFormat();
      
      console.log('[PlanService] getPlanById - formato editor:', {
        planId: id,
        nodeCount: editorFormat.nodes?.length,
        hasVisual: plan.hasVisualData()
      });
      
      return {
        success: true,
        data: {
          ...planData,
          ...plan.toFrontendFormat(),
          workflowData: editorFormat
        }
      };
    } catch (error) {
      console.error(`[PlanService] getPlanById - ERROR (${id}):`, error);
      throw error;
    }
  }

  /**
   * Obtener plan para N8N - SOLO CORE DATA
   */
  async getPlanForN8N(id) {
    try {
      console.log('[PlanService] getPlanForN8N - INICIO', { id });
      
      const planData = await this.provider.getPlanById(id);
      if (!planData) {
        throw new Error(`Plan ${id} no encontrado`);
      }
      
      // Crear modelo y extraer solo core
      const plan = new PlanModel(planData);
      const n8nFormat = plan.toN8NFormat();
      
      console.log('[PlanService] getPlanForN8N - formato N8N:', {
        planId: id,
        nodeCount: n8nFormat.nodes?.length,
        hasVisual: false // Nunca incluye visual
      });
      
      return {
        success: true,
        data: n8nFormat
      };
    } catch (error) {
      console.error(`[PlanService] getPlanForN8N - ERROR (${id}):`, error);
      throw error;
    }
  }

  /**
   * Crear plan nuevo
   */
  async createPlan(planData) {
    try {
      console.log('[PlanService] createPlan - INICIO', {
        name: planData.name,
        source: planData.source || planData.createdBy,
        hasPureData: !!planData.planDataPure,
        hasVisualData: !!planData.planDataVisual
      });
      
      // ✅ NUEVA LÓGICA: Usar datos ya preparados por PlanModel
      const createdPlan = await this.provider.createPlan(planData);
      
      console.log('[PlanService] createPlan - plan creado:', createdPlan.id);
      
      return {
        success: true,
        data: createdPlan
      };
    } catch (error) {
      console.error('[PlanService] createPlan - ERROR:', error);
      throw error;
    }
  }

  /**
   * Actualizar plan existente
   */
  async updatePlan(id, planData) {
    try {
      console.log('[PlanService] updatePlan - INICIO', { 
        id, 
        hasPureData: !!planData.planDataPure,
        hasVisualData: !!planData.planDataVisual 
      });
      
      // ✅ NUEVA LÓGICA: Usar datos ya preparados por PlanModel
      const updatedPlan = await this.provider.updatePlan(id, planData);
      
      console.log('[PlanService] updatePlan - plan actualizado:', id);
      
      return {
        success: true,
        data: updatedPlan
      };
    } catch (error) {
      console.error(`[PlanService] updatePlan - ERROR (${id}):`, error);
      throw error;
    }
  }

  /**
   * Eliminar plan
   */
  async deletePlan(id) {
    try {
      console.log('[PlanService] deletePlan - INICIO', { id });
      
      await this.provider.deletePlan(id);
      
      console.log('[PlanService] deletePlan - plan eliminado:', id);
      
      return {
        success: true,
        message: 'Plan eliminado correctamente'
      };
    } catch (error) {
      console.error(`[PlanService] deletePlan - ERROR (${id}):`, error);
      throw error;
    }
  }

  /**
   * NUEVA FUNCIONALIDAD: Convertir plan desde Editor para guardado
   */
  async savePlanFromEditor(editorData) {
    try {
      console.log('[PlanService] savePlanFromEditor - INICIO', {
        nodeCount: editorData.nodes?.length,
        edgeCount: editorData.edges?.length
      });
      
      // ✅ NUEVA LÓGICA: Usar PlanModel para generar formatos duales
      const planModel = PlanModel.fromEditor({
        ...editorData,
        createdBy: 'editor' // camelCase
      });
      
      const backendData = planModel.toBackendFormat();
      
      if (editorData.id) {
        return await this.updatePlan(editorData.id, backendData);
      } else {
        return await this.createPlan(backendData);
      }
    } catch (error) {
      console.error('[PlanService] savePlanFromEditor - ERROR:', error);
      throw error;
    }
  }

  /**
   * ✅ FUNCIÓN PÚBLICA: Guardar plan desde N8N
   * USAR ESTA función para guardar planes generados por N8N
   */
  async savePlanFromN8N(n8nData) {
    try {
      console.log('[PlanService] savePlanFromN8N - INICIO', {
        stepCount: n8nData.steps?.length,
        objective: n8nData.objective
      });
      
      // ✅ ENVIAR EN CAMELCASE: Backend acepta camelCase
      const backendData = {
        name: n8nData.name || 'Plan generado por N8N',
        objective: n8nData.objective,
        planDataPure: n8nData,
        planDataVisual: null, // Se auto-generará en el backend
        createdBy: 'n8n',
        userId: n8nData.userId
      };
      
      return await this.createPlan(backendData);
    } catch (error) {
      console.error('[PlanService] savePlanFromN8N - ERROR:', error);
      throw error;
    }
  }

  /**
   * Buscar planes
   */
  async searchPlans(searchTerm) {
    try {
      console.log('[PlanService] searchPlans - INICIO', { searchTerm });

      // El ámbito lo pone el JWT en el backend (#576): no hay userId que pasar.
      const results = await this.provider.searchPlans(searchTerm);

      return {
        success: true,
        data: results
      };
    } catch (error) {
      console.error('[PlanService] searchPlans - ERROR:', error);
      throw error;
    }
  }

  /**
   * Obtener planes por usuario
   */
  async getPlansByUser(userId) {
    try {
      console.log('[PlanService] getPlansByUser - INICIO', { userId: userId?.substring(0, 6) + '...' });
      
      const plans = await this.provider.getPlansByUser(userId);
      
      return {
        success: true,
        data: plans
      };
    } catch (error) {
      console.error('[PlanService] getPlansByUser - ERROR:', error);
      throw error;
    }
  }

  /**
   * DEBUG: Obtener información de debug de un plan
   */
  async getDebugInfo(id) {
    try {
      const planData = await this.provider.getPlanById(id);
      if (!planData) return null;
      
      const plan = new PlanModel(planData);
      return plan.getDebugInfo();
    } catch (error) {
      console.error(`[PlanService] getDebugInfo - ERROR (${id}):`, error);
      return null;
    }
  }
}

// Instancia singleton
const planService = new PlanService();

export default planService;